import { CommonModule } from '@angular/common'
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { NbButtonModule, NbCardModule, NbFormFieldModule, NbIconModule, NbInputModule, NbSelectModule, NbTagModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { EmailTemplateType } from '../../enums/email-composer.enum'
import { EmailTemplate } from '../../interfaces/email-template.interface'
import { EmailComposerStateService } from '../../services/email-composer-state.service'
import { EmailComposerService } from '../../services/email-composer.service'

@Component({
  selector: 'ngx-template-list',
  templateUrl: './template-list.component.html',
  styleUrls: ['./template-list.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NbCardModule, NbButtonModule, NbFormFieldModule, NbInputModule, NbSelectModule, NbIconModule, NbTagModule]
})
export class TemplateListComponent implements OnInit, OnDestroy {
  @Output() editTemplate = new EventEmitter<number>()
  @Output() createTemplate = new EventEmitter<void>()

  templates: EmailTemplate[] = []
  loading = false

  // Global search is provided via ComposerHeader; still exposing local filters
  activeFilterControl = new FormControl(true)
  systemTemplateControl = new FormControl('')

  templateTypes = Object.values(EmailTemplateType)

  private destroy$ = new Subject<void>()
  private currentSearch = ''

  constructor(private emailComposerService: EmailComposerService, private state: EmailComposerStateService) {}

  ngOnInit(): void {
    this.loadTemplates()
    this.setupSearch()
    // React to shared search (debounced) and keep latest value
    this.state.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.currentSearch = value || ''
      this.loadTemplates()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  setupSearch(): void {
    this.activeFilterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadTemplates())

    this.systemTemplateControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadTemplates())
  }

  loadTemplates(): void {
    this.loading = true

    const search: string | undefined = this.currentSearch || undefined
    const is_active = this.activeFilterControl.value
    const is_system_template = this.systemTemplateControl.value === '' ? undefined : this.systemTemplateControl.value === 'true'

    this.emailComposerService
      .getTemplates(search, is_active, is_system_template)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.templates = response.data
          this.loading = false
        },
        error: (error) => {
          console.error('Error loading templates:', error)
          this.loading = false
        }
      })
  }

  onEditTemplate(template: EmailTemplate): void {
    this.editTemplate.emit(template.id!)
  }

  onCreateTemplate(): void {
    this.createTemplate.emit()
  }

  onDuplicateTemplate(template: EmailTemplate): void {
    if (template.id) {
      const duplicateData = {
        name: `${template.name} Copy`,
        description: template.description
      }

      this.emailComposerService
        .duplicateTemplate(template.id, duplicateData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadTemplates()
          },
          error: (error) => {
            console.error('Error duplicating template:', error)
          }
        })
    }
  }

  onDeleteTemplate(template: EmailTemplate): void {
    if (template.id && confirm(`Are you sure you want to delete "${template.name}"?`)) {
      this.emailComposerService
        .deleteTemplate(template.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadTemplates()
          },
          error: (error) => {
            console.error('Error deleting template:', error)
          }
        })
    }
  }

  onToggleStatus(template: EmailTemplate): void {
    if (template.id) {
      const updatedTemplate = { is_active: !template.is_active }

      this.emailComposerService
        .updateTemplate(template.id, updatedTemplate)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const index = this.templates.findIndex((t) => t.id === template.id)
            if (index !== -1) {
              this.templates[index] = response.data
            }
          },
          error: (error) => {
            console.error('Error toggling template status:', error)
          }
        })
    }
  }

  getTemplateTypeLabel(type: string): string {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  getStatusColor(isActive: boolean): string {
    return isActive ? 'success' : 'basic'
  }

  getStatusText(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive'
  }

  getTotalTemplates(): number {
    return this.templates.length
  }
}
